import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import MainWrapper from "../../../MainWrapper";
import { Link, useNavigate } from "react-router-dom";
import Icon from "react-icons-kit";
import { ic_arrow_back } from "react-icons-kit/md";
import { Table, Button, Form } from "react-bootstrap";
import Loader from "../../../layouts/Loader";
import axios from "axios";
import { API_URL } from "../../../../config";
import { toast } from "react-toastify";

// Sunday = 0, matching .NET's DayOfWeek so the ordinal can be stored as-is and the
// scheduler never has to parse a day name.
const DAYS = [
  { label: "Sunday", value: 0 },
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
];

const CADENCES = [
  { label: "Weekly", value: "Weekly" },
  { label: "Daily", value: "Daily" },
];

// Quarter-hour steps. A finer grain would imply the send is precise to the minute,
// which it is not — the scheduler polls, so the real resolution is the poll interval.
const MINUTES = [0, 15, 30, 45];

// A stored minute that is not one of the quarter-hours still has to be selectable,
// otherwise the dropdown falls back to showing :00 while the server holds something
// else — the screen then misreports the schedule, and pressing Save silently
// overwrites the real value with :00. Seed values from appsettings can be any minute,
// so this is reachable rather than theoretical.
const minuteOptions = (current) => {
  const n = parseInt(current, 10);
  if (Number.isNaN(n) || MINUTES.includes(n)) return MINUTES;
  return [...MINUTES, n].sort((a, b) => a - b);
};

const hourLabel = (h) => {
  const suffix = h < 12 ? "AM" : "PM";
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}:00 ${suffix}`;
};

const EmailReportSchedules = ({ auth: { auth, isAuthenticated } }) => {
  const [rows, setRows] = useState([]);
  const [timeZone, setTimeZone] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const navigate = useNavigate();

  const authHeader = () => ({ headers: { Authorization: `Bearer ${auth[0].token}` } });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/User/api/EmailReportSchedule/GetSchedules`, authHeader()),
      axios.get(`${API_URL}/User/api/EmailReportSchedule/GetTimeZone`, authHeader()),
    ])
      .then(([schedules, tz]) => {
        setRows(schedules?.data?.result || []);
        setTimeZone((tz?.data?.result || [])[0] || null);
      })
      .catch(() => toast.error("Could not load report schedules."))
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  // Edits are held locally per row and committed on that row's Save, so a mistake in
  // one report cannot be saved by pressing Save on another.
  const updateRow = (reportId, field, value) => {
    setRows((current) =>
      current.map((r) => (r.reportId === reportId ? { ...r, [field]: value, dirty: true } : r))
    );
  };

  const handleSave = (row) => {
    setSavingId(row.reportId);
    axios
      .post(
        `${API_URL}/User/api/EmailReportSchedule/SaveSchedule`,
        {
          reportId: row.reportId,
          cadence: row.cadence,
          dayOfWeek: parseInt(row.dayOfWeek, 10),
          sendHourLocal: parseInt(row.sendHourLocal, 10),
          sendMinuteLocal: parseInt(row.sendMinuteLocal, 10),
          catchUpHours: parseInt(row.catchUpHours, 10) || 24,
        },
        authHeader()
      )
      .then((res) => {
        // The API returns the full refreshed list, so the summary column reflects what
        // was actually stored rather than what was typed.
        setRows(res?.data?.result || []);
        toast.success(`${row.label} schedule saved.`);
      })
      .catch((err) => {
        const msg = err?.response?.data?.result?.[0] || "Could not save the schedule.";
        toast.error(msg);
      })
      .finally(() => setSavingId(null));
  };

  return (
    <Fragment>
      <MainWrapper>
        <Loader loading={loading}>
          <div className="container">
            <div className="sub-header mt-5 mb-3">
              <h2>Automatic Email Reports</h2>
              <Link className="btn btn-primary me-1" to="/admin">
                <Icon icon={ic_arrow_back} />
                Back
              </Link>
            </div>

            <p className="text-muted">
              When each report is sent. Who receives it is set per role under{" "}
              <Link to="/role">Roles</Link>.
              {timeZone ? (
                <>
                  {" "}Times are {timeZone.label || timeZone.id}
                  {timeZone.currentLocalTime ? ` — currently ${timeZone.currentLocalTime}` : ""}.
                  {" "}Daylight saving is handled, so a report set for 7:00 AM stays at
                  7:00 AM all year.
                </>
              ) : null}
            </p>

            {rows.length === 0 && !loading ? (
              <p className="text-muted">No reports are registered.</p>
            ) : (
              <Table bordered hover responsive>
                <thead>
                  <tr>
                    <th>Report</th>
                    <th>Frequency</th>
                    <th>Day</th>
                    <th>Time</th>
                    <th>Sends</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const isDaily = row.cadence === "Daily";
                    return (
                      <tr key={row.reportId}>
                        <td className="align-middle">{row.label}</td>
                        <td>
                          <Form.Select
                            value={row.cadence}
                            onChange={(e) => updateRow(row.reportId, "cadence", e.target.value)}
                          >
                            {CADENCES.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                        <td>
                          {/* Disabled rather than hidden for Daily, so the column does
                              not jump around as the frequency changes. */}
                          <Form.Select
                            value={row.dayOfWeek}
                            disabled={isDaily}
                            onChange={(e) => updateRow(row.reportId, "dayOfWeek", e.target.value)}
                          >
                            {DAYS.map((d) => (
                              <option key={d.value} value={d.value}>
                                {d.label}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                        <td>
                          <div className="d-flex">
                            <Form.Select
                              className="me-1"
                              value={row.sendHourLocal}
                              onChange={(e) =>
                                updateRow(row.reportId, "sendHourLocal", e.target.value)
                              }
                            >
                              {Array.from({ length: 24 }, (_, h) => (
                                <option key={h} value={h}>
                                  {hourLabel(h)}
                                </option>
                              ))}
                            </Form.Select>
                            <Form.Select
                              value={row.sendMinuteLocal}
                              onChange={(e) =>
                                updateRow(row.reportId, "sendMinuteLocal", e.target.value)
                              }
                            >
                              {minuteOptions(row.sendMinuteLocal).map((m) => (
                                <option key={m} value={m}>
                                  {`:${String(m).padStart(2, "0")}`}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                        </td>
                        <td className="align-middle text-muted">
                          {row.dirty ? <em>unsaved</em> : row.summary}
                        </td>
                        <td className="align-middle">
                          <Button
                            variant="primary"
                            disabled={savingId === row.reportId}
                            onClick={() => handleSave(row)}
                          >
                            {savingId === row.reportId ? "Saving…" : "Save"}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}

            <p className="text-muted small">
              A report covers the Friday-to-Thursday week that closed before it is sent, so
              the team has Friday and the weekend to correct anything before it is reviewed.
              Changing a time does not re-send a report that has already gone out for the
              current period.
            </p>
          </div>
        </Loader>
      </MainWrapper>
    </Fragment>
  );
};

const mapStateToProps = ({ auth }) => ({ auth });

export default connect(mapStateToProps)(EmailReportSchedules);
